import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";

export default function TeamFormDialog({
  open,
  onClose,
  onSubmit,
  initialData,
  loading,
  categories,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [teamPhotoFile, setTeamPhotoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [teamPhotoPreview, setTeamPhotoPreview] = useState("");

  useEffect(() => {
    if (initialData) {
      setName(initialData.name || "");
      setCategoryId(
        initialData.category_id ? String(initialData.category_id) : ""
      );
      setLogoFile(null);
      setTeamPhotoFile(null);
      setLogoPreview(initialData.logo || "");
      setTeamPhotoPreview(initialData.team_photo || "");
    } else {
      setName("");
      setCategoryId(categories[0] ? String(categories[0].id) : "");
      setLogoFile(null);
      setTeamPhotoFile(null);
      setLogoPreview("");
      setTeamPhotoPreview("");
    }
  }, [initialData, open, categories]);

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleTeamPhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setTeamPhotoFile(file);
    setTeamPhotoPreview(URL.createObjectURL(file));
  };

  const handleSave = () => {
    onSubmit({
      name: name.trim(),
      category_id: categoryId,
      logo: logoFile,
      team_photo: teamPhotoFile,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: "18px",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "primary.main",
          color: "#fff",
          fontWeight: 700,
          fontSize: "1.05rem",
          mb: 3,
        }}
      >
        {initialData ? "Editar equipo" : "Nuevo equipo"}
      </DialogTitle>

      <DialogContent sx={{ pt: 2.2 }}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Nombre del equipo"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <TextField
            select
            fullWidth
            label="Categoría"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            {categories.map((category) => (
              <MenuItem key={category.id} value={String(category.id)}>
                {category.name}
              </MenuItem>
            ))}
          </TextField>

          <Stack spacing={1}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadRoundedIcon />}
              sx={{
                alignSelf: "flex-start",
              }}
            >
              {logoFile || logoPreview ? "Cambiar escudo" : "Subir escudo"}

              <input
                hidden
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/heic,image/heif,image/webp"
                onChange={handleLogoChange}
              />
            </Button>

            {logoPreview && (
              <Box
                sx={{
                  width: 92,
                  height: 92,
                  borderRadius: "18px",
                  overflow: "hidden",
                  border: "1px solid rgba(140,15,44,0.12)",
                  backgroundColor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={logoPreview}
                  alt="escudo equipo"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}

            <Typography
              sx={{
                fontSize: "0.82rem",
                color: "text.secondary",
              }}
            >
              Puedes subir PNG, JPG, JPEG, HEIC o WEBP.
            </Typography>
          </Stack>

          <Stack spacing={1}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadRoundedIcon />}
              sx={{
                alignSelf: "flex-start",
              }}
            >
              {teamPhotoFile || teamPhotoPreview
                ? "Cambiar foto grupal"
                : "Subir foto grupal"}

              <input
                hidden
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/heic,image/heif,image/webp"
                onChange={handleTeamPhotoChange}
              />
            </Button>

            {teamPhotoPreview && (
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 320,
                  height: 180,
                  borderRadius: "18px",
                  overflow: "hidden",
                  border: "1px solid rgba(140,15,44,0.12)",
                  backgroundColor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={teamPhotoPreview}
                  alt="foto grupal del equipo"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            )}

            <Typography
              sx={{
                fontSize: "0.82rem",
                color: "text.secondary",
              }}
            >
              Puedes subir PNG, JPG, JPEG, HEIC o WEBP.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.4,
          gap: 1,
        }}
      >
        <Button onClick={onClose} variant="outlined" disabled={loading}>
          Cancelar
        </Button>

        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || !name.trim() || !categoryId}
        >
          {loading
            ? "Guardando..."
            : initialData
            ? "Actualizar"
            : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}